import React, { useState, useEffect } from "react";
import { Button, Grid, Paper, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Typography, Box, Collapse, Chip, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { NotificationManager } from "react-notifications";
import { GenerateInvoice } from "../../services/InvoiceService";
import {
  UploadToCMDP,
  GenerateAiSummary,
  UpdateAiSummary,
  MarkAiSummaryReviewed,
  GenerateLabReport,
  EmailLabReport,
} from "../../services/OrderService";
import UserService from "../../../globel-constants/UserService";
import { GetUsers } from "../../../services/usersService";
import SampleScannedNew from "../../../components/checkin/SampleScannedNew";
import SampleScanned from "../../../components/checkin/sampleScanned";

// API errors carry a { message } body; ky wraps the response, getBlob throws the Response itself.
const getErrorMessage = async (error) => {
  try {
    const body = await (error?.response ?? error).json();
    return body?.message || error?.message || "";
  } catch {
    return error?.message || "";
  }
};

const LabOrderActions = ({ orderNumber, orderType, accountNumber, aiSummary, aiSummaryReviewed, expanded, onCheckInSubmitted }) => {
  const [loggedInUser, setLoggedInUser] = useState();
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkInUsers, setCheckInUsers] = useState([]);
  const [generatingAiSummary, setGeneratingAiSummary] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState("");
  const [savingSummary, setSavingSummary] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  const [emailingReport, setEmailingReport] = useState(false);

  const isEtkOrder = (orderType || "").trim().toUpperCase() === "ETK";
  // AI summary applies to every ETK-family client type (ETK, ETK Distributor, Lab and ETK, ...).
  const isEtkClient = (orderType || "").toUpperCase().includes("ETK");

  // ENB-592: only these users may generate, edit or mark the AI summary reviewed.
  // Admin and SuperAdmin bypass the specific permission.
  // Same permission lookup shape VLP.js's route guards use (user.permissions.X
  // or a flat user.X, whichever the login response actually carried).
  const canEditAiSummary = !!(
    loggedInUser?.permissions?.canEditOrderAiSummary ||
    loggedInUser?.canEditOrderAiSummary ||
    loggedInUser?.permissions?.isAdmin ||
    loggedInUser?.isAdmin ||
    loggedInUser?.permissions?.isSuperAdmin ||
    loggedInUser?.isSuperAdmin
  );

  useEffect(() => {
    let user = UserService.getUserInfoFromLocalStorage();
    setLoggedInUser(user);
  }, []);

  const handleUpdateCheckIn = async () => {
    if (!isEtkOrder && checkInUsers.length === 0) {
      try {
        let u = await GetUsers("SHIPPING");
        setCheckInUsers(u);
      } catch (error) {
        NotificationManager.error(
          "Error getting the list of users. " + error.message
        );
        return;
      }
    }
    setCheckInOpen(true);
  };

  const handleCheckInClosed = () => {
    setCheckInOpen(false);
    if (onCheckInSubmitted) onCheckInSubmitted();
  };

  const handleNewInvoice = async () => {
    createInvoice();
    console.log("New Invoice for Order:", orderNumber);
  };

  const handleSendToCMDP = async () => {
    console.log("Sending Order to CMDP:", orderNumber);
    sendToCMDP();
  };

  const createInvoice = async () => {
    try {
      let response = await GenerateInvoice({
        invoiceNumber: orderNumber.toString(),
        reviewedBy: loggedInUser.operator,
      });

      const data = await response.json();
      NotificationManager.success(
        "Invoice created successfully" + " Invoice Number: " + data.results
      );
      window.open(`/vlp/invoices/edit/${data.results}`, "_blank");
    } catch (error) {
      NotificationManager.error("Error creating Invoice " + error.message);
    }
  };

  const sendToCMDP = async () => {
    try {
      let response = await UploadToCMDP(orderNumber.toString());

      const data = await response.json();
      if (data.isSuccessful) {
        NotificationManager.success("Order successfully send to CMDP");
      } else {
        NotificationManager.error(
          "Error sending Order to CMDP " + data.message
        );
      }
    } catch (error) {
      NotificationManager.error("Error creating Invoice " + error.message);
    }
  };

  const handleGenerateAiSummary = async () => {
    setGeneratingAiSummary(true);
    try {
      await GenerateAiSummary(orderNumber.toString());
      NotificationManager.success("AI summary generated successfully");
      // Reuses the same "refresh the order" callback the check-in dialog uses,
      // the freshly generated summary lives on the order record itself.
      if (onCheckInSubmitted) onCheckInSubmitted();
    } catch (error) {
      NotificationManager.error("Error generating AI summary. " + error.message);
    } finally {
      setGeneratingAiSummary(false);
    }
  };

  const handleGenerateLabReport = async () => {
    setGeneratingReport(true);
    try {
      await GenerateLabReport(orderNumber.toString());
    } catch (error) {
      NotificationManager.error("Error generating lab report. " + (await getErrorMessage(error)));
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleEmailLabReport = async () => {
    setEmailConfirmOpen(false);
    setEmailingReport(true);
    try {
      await EmailLabReport(orderNumber.toString());
      NotificationManager.success("Lab report email queued");
      // Status and date reported change on the server, reload the order to show them.
      if (onCheckInSubmitted) onCheckInSubmitted();
    } catch (error) {
      NotificationManager.error("Error emailing lab report. " + (await getErrorMessage(error)));
    } finally {
      setEmailingReport(false);
    }
  };

  const handleEditSummary = () => {
    setSummaryDraft(aiSummary || "");
    setEditingSummary(true);
    setShowSummary(true);
  };

  const handleSaveSummary = async () => {
    setSavingSummary(true);
    try {
      await UpdateAiSummary(orderNumber.toString(), summaryDraft);
      NotificationManager.success("AI summary updated");
      setEditingSummary(false);
      if (onCheckInSubmitted) onCheckInSubmitted();
    } catch (error) {
      NotificationManager.error("Error updating AI summary. " + (await getErrorMessage(error)));
    } finally {
      setSavingSummary(false);
    }
  };

  const handleMarkReviewed = async () => {
    setSavingSummary(true);
    try {
      await MarkAiSummaryReviewed(orderNumber.toString());
      NotificationManager.success("AI summary marked as reviewed");
      if (onCheckInSubmitted) onCheckInSubmitted();
    } catch (error) {
      NotificationManager.error("Error marking AI summary as reviewed. " + (await getErrorMessage(error)));
    } finally {
      setSavingSummary(false);
    }
  };

  const handleComingSoon = (actionName) => {
    NotificationManager.success(`${actionName} Coming Soon.`);
    console.log(`${actionName} action triggered for Order:`, orderNumber);
  };

  return (
    <div>
      {/* Padding lives inside the Collapse so a collapsed Actions panel takes no space */}
      <Collapse in={expanded}>
      {/* Buttons are grouped by action type; each group shares one color. */}
      <Grid container spacing={2} sx={{ p: "10px" }}>
        {/* Create */}
        <Grid item sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Button variant="outlined" size="small" color="success" onClick={() => handleComingSoon("New Order")}>
            New Order
          </Button>
          <Button variant="outlined" size="small" color="success" onClick={() => handleComingSoon("Duplicate Order")}>
            Duplicate Order
          </Button>
          <Button variant="outlined" size="small" color="success" onClick={() => handleComingSoon("Use Template")}>
            Use Template
          </Button>
          <Button variant="outlined" size="small" color="success" onClick={() => handleComingSoon("Use Template Samples")}>
            Use Template Samples
          </Button>
          <Button variant="outlined" size="small" color="success" onClick={handleNewInvoice}>
            Create Invoice
          </Button>
        </Grid>

        {/* Generate */}
        <Grid item sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleGenerateLabReport}
            disabled={generatingReport}
          >
            {generatingReport ? "Generating..." : "Generate Lab Report"}
          </Button>
          {isEtkClient && (
            <Tooltip title={canEditAiSummary ? "" : "AI summary permission required"}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={handleGenerateAiSummary}
                  disabled={generatingAiSummary || !canEditAiSummary}
                >
                  {generatingAiSummary ? "Generating..." : "Generate AI Summary"}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button variant="outlined" size="small" color="primary" onClick={() => handleComingSoon("Barcode")}>
            Barcode
          </Button>
          <Button variant="outlined" size="small" color="primary" onClick={() => handleComingSoon("Chain")}>
            Chain
          </Button>
        </Grid>

        {/* Send */}
        <Grid item sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            onClick={() => setEmailConfirmOpen(true)}
            disabled={emailingReport}
          >
            {emailingReport ? "Sending..." : "Email Lab Report"}
          </Button>
          <Button variant="outlined" size="small" color="secondary" onClick={handleSendToCMDP}>
            Send To CMDP
          </Button>
        </Grid>

        {/* Other */}
        <Grid item sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Button variant="outlined" size="small" color="warning" onClick={handleUpdateCheckIn}>
            Update Check-in Sheet
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            onClick={() => window.open(`/vlp/quotes?account=${encodeURIComponent((accountNumber || "").trim())}`, "_blank")}
            disabled={!accountNumber?.trim()}
          >
            Jump To Quotes
          </Button>
        </Grid>
      </Grid>

      {isEtkClient && aiSummary && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            onClick={() => setShowSummary(!showSummary)}
            startIcon={showSummary ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: "none" }}
          >
            AI Summary
          </Button>
          <Chip
            label={aiSummaryReviewed ? "Reviewed" : "Not Reviewed"}
            size="small"
            color={aiSummaryReviewed ? "success" : "warning"}
            variant="outlined"
            sx={{ ml: 1 }}
          />
          <Collapse in={showSummary}>
            {editingSummary ? (
              <Box sx={{ px: 1, py: 1 }}>
                <TextField
                  value={summaryDraft}
                  onChange={(e) => setSummaryDraft(e.target.value)}
                  multiline
                  minRows={8}
                  fullWidth
                  size="small"
                />
                <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                  <Button variant="contained" size="small" onClick={handleSaveSummary} disabled={savingSummary}>
                    {savingSummary ? "Saving..." : "Save"}
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => setEditingSummary(false)} disabled={savingSummary}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", px: 1, py: 1 }}>
                  {aiSummary}
                </Typography>
                {canEditAiSummary && (
                  <Box sx={{ px: 1, pb: 1, display: "flex", gap: 1 }}>
                    <Button variant="outlined" size="small" onClick={handleEditSummary} disabled={savingSummary}>
                      Edit
                    </Button>
                    {!aiSummaryReviewed && (
                      <Button variant="contained" size="small" onClick={handleMarkReviewed} disabled={savingSummary}>
                        Mark As Reviewed
                      </Button>
                    )}
                  </Box>
                )}
              </>
            )}
          </Collapse>
        </Box>
      )}
      </Collapse>

      <Dialog open={emailConfirmOpen} onClose={() => setEmailConfirmOpen(false)}>
        <DialogTitle>Email Lab Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Email the lab report for order {orderNumber}? If the order is not already Paid, it will be
            set to Paid and the date reported captured.
          </Typography>
          {isEtkClient && aiSummary && !aiSummaryReviewed && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              The AI summary has not been reviewed, so it will not be included in the report.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEmailLabReport}>Send</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={checkInOpen} onClose={handleCheckInClosed} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Update Check-in Sheet
          <IconButton onClick={handleCheckInClosed} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {checkInOpen && (
            isEtkOrder ? (
              <SampleScannedNew scannedId={orderNumber} onBack={handleCheckInClosed} />
            ) : (
              <SampleScanned
                scanData={orderNumber}
                users={checkInUsers}
                clearScan={handleCheckInClosed}
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LabOrderActions;
